import React, { useMemo } from 'react';

const PlayPreview = React.memo(({ players = [], routes = [], teamType = 'Offense' }) => {
    // Only show offense for offensive plays (or as requested)
    const previewPlayers = players.filter(p => p.type?.toLowerCase() === 'offense');

    return (
        <div className="qr-preview-container">
            <svg viewBox="0 10 100 80" className="qr-preview-svg">
                {/* Yard Lines (Simplified) */}
                <line x1="0" y1="30" x2="100" y2="30" stroke="#334155" strokeWidth="0.2" />
                <line x1="0" y1="50" x2="100" y2="50" stroke="#334155" strokeWidth="0.2" />
                <line x1="0" y1="70" x2="100" y2="70" stroke="#334155" strokeWidth="0.2" />

                {/* Routes */}
                {routes.map(r => {
                    // Only show routes for players we are previewing
                    const owner = players.find(p => p.id === r.playerId);
                    if (!owner || owner.type?.toLowerCase() !== 'offense') return null;

                    const pts = r.pointsAbs || r.points || [];
                    if (pts.length < 2) return null;
                    const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                    return (
                        <path
                            key={r.id}
                            d={d}
                            fill="none"
                            stroke={r.color || 'yellow'}
                            strokeWidth="1"
                            strokeDasharray={r.type === 'option' ? '1,1' : 'none'}
                        />
                    );
                })}

                {/* Players */}
                {previewPlayers.map(p => (
                    <circle
                        key={p.id}
                        cx={p.x}
                        cy={p.y}
                        r="2"
                        fill={p.color || '#3b82f6'}
                        stroke="white"
                        strokeWidth="0.3"
                    />
                ))}
            </svg>
        </div>
    );
});

const StarRating = React.memo(({ rating, onChange, interactive = false }) => {
    // For display, round to nearest 0.5
    const displayRating = Math.round(rating * 2) / 2;

    return (
        <div className={`qr-stars ${interactive ? 'interactive' : ''}`}>
            {[1, 2, 3, 4, 5].map(star => {
                const isFull = star <= displayRating;
                const isHalf = !isFull && (star - 0.5 <= displayRating);

                return (
                    <span
                        key={star}
                        className={`qr-star ${isFull ? 'filled' : (isHalf ? 'half-filled' : '')}`}
                        onClick={(e) => {
                            if (!interactive) return;
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const isLeft = x < rect.width / 2;

                            let newVal = isLeft ? star - 0.5 : star;
                            // Toggle off if clicking the same value
                            if (newVal === rating) newVal = 0;
                            onChange(newVal);
                        }}
                    >
                        ★
                    </span>
                );
            })}
            {interactive && rating === 0 && <span className="text-[10px] text-gray-500 ml-1">Rate this play</span>}
        </div>
    );
});


export default function QuickRoutesView({
    playbook, searchTerm, filterType, filterFormation, onOpen, onUpdateRating, onMovePlay,
    onDuplicatePlay, onDeletePlay, canDuplicate = true, canDelete = true,
    activeFolderId, onAssignPlays,
    sortBy, setSortBy,
    addToast // Passed from App.js for feedback
}) {
    const [showPreviews, setShowPreviews] = React.useState(true);
    const [selectedPlays, setSelectedPlays] = React.useState(new Set());

    const activeFolderIds = useMemo(() => {
        if (!activeFolderId) return null;
        const allFolders = playbook?.folders || {};
        const descendants = [activeFolderId];
        const getChildren = (pid) => {
            Object.values(allFolders).forEach(f => {
                if (f.parentId === pid) {
                    descendants.push(f.id);
                    getChildren(f.id);
                }
            });
        };
        getChildren(activeFolderId);
        return new Set(descendants);
    }, [activeFolderId, playbook?.folders]);

    const items = useMemo(() => {
        const plays = playbook?.plays || {};
        const list = Object.entries(plays).map(([id, p]) => {
            const dateVal = p.updatedAt?.toDate ? p.updatedAt.toDate() : (p.updatedAt || p.savedAt || p.date || null);
            const createdVal = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt || null);

            return {
                id, // Firestore ID or Local Name
                name: p.playName || id, // Display Name
                formation: p.formation || 'MISC',
                type: p.teamType || 'Offense',
                savedAt: dateVal,
                createdAt: createdVal,
                createdByName: p.createdByName,
                updatedByName: p.updatedByName,
                rating: p.rating || 0,
                ratingCount: p.ratingCount || 0,
                order: p.order ?? 0,
                folderId: p.folderId || null,
                rawData: p
            };
        });

        // Filter
        let filtered = list.filter(it => {
            // Folder Filter (Recursive)
            if (activeFolderIds && !activeFolderIds.has(it.folderId)) return false;

            if (filterType.size) {
                const normalizedFilter = Array.from(filterType).map(t => t.toLowerCase());
                if (!normalizedFilter.includes(it.type.toLowerCase())) return false;
            }
            if (filterFormation.size && !filterFormation.has(it.formation)) return false;
            if (searchTerm && !it.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
            return true;
        });

        // Sort
        filtered.sort((a, b) => {
            if (sortBy === 'rating') {
                if (b.rating !== a.rating) return b.rating - a.rating;
                return a.order - b.order;
            }
            if (sortBy === 'newest') {
                // Creation date (desc)
                return new Date(b.createdAt || b.savedAt || 0) - new Date(a.createdAt || a.savedAt || 0);
            }
            if (sortBy === 'updated') {
                // Update date (desc)
                return new Date(b.savedAt || 0) - new Date(a.savedAt || 0);
            }
            return a.order - b.order;
        });

        return filtered;
    }, [playbook, searchTerm, filterType, filterFormation, sortBy, activeFolderId]);

    const toggleSelection = (e, name) => {
        e.stopPropagation();
        setSelectedPlays(prev => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    const handleDragStart = (e, name) => {
        const dragList = selectedPlays.has(name)
            ? Array.from(selectedPlays)
            : [name];

        e.dataTransfer.setData("application/json", JSON.stringify({ names: dragList }));
        e.dataTransfer.effectAllowed = "move";
    };

    return (
        <div className="qr-library">
            <div className="qr-library-header">
                <div className="qr-library-controls">
                    <div className="qr-control-group">
                        <label>Sort By:</label>
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="qr-select">
                            <option value="order">Manual Order</option>
                            <option value="rating">Highest Rated</option>
                            <option value="newest">Newest First</option>
                            <option value="updated">Recently Updated</option>
                        </select>
                    </div>

                    {selectedPlays.size > 0 && (
                        <div className="qr-selection-badge">
                            {selectedPlays.size} Selected
                            <button onClick={() => setSelectedPlays(new Set())} className="ml-2 text-red-400">Clear</button>
                        </div>
                    )}

                    <div className="flex items-center gap-4">
                        <label className="qr-preview-toggle">
                            <input
                                type="checkbox"
                                checked={showPreviews}
                                onChange={(e) => setShowPreviews(e.target.checked)}
                            />
                            <span>Show Previews</span>
                        </label>
                    </div>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="text-6xl mb-4 opacity-20">📁</div>
                    <h3 className="text-xl font-bold text-gray-500">No Quick Routes Found</h3>
                    <p className="text-sm text-gray-600 mt-2 max-w-xs text-center">Try adjusting your filters or creating a new play in the builder.</p>
                </div>
            ) : (
                <div className="qr-grid">
                    {items.map((it, idx) => (
                        <div
                            key={it.id}
                            className={`qr-card-wrapper ${showPreviews ? 'with-preview' : ''} ${selectedPlays.has(it.id) ? 'selected' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, it.id)}
                        >
                            <div className="qr-card-select-overlay">
                                <input
                                    type="checkbox"
                                    checked={selectedPlays.has(it.id)}
                                    onChange={(e) => toggleSelection(e, it.id)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>

                            <div
                                className="qr-card cursor-pointer"
                                onClick={() => onOpen(it.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onOpen(it.id);
                                    }
                                }}
                            >
                                {showPreviews && (
                                    <PlayPreview
                                        players={it.rawData.players}
                                        routes={it.rawData.routes}
                                        teamType={it.type}
                                    />
                                )}
                                <div className="qr-info">
                                    <div className="qr-card-rating flex items-center gap-1.5 mb-1">
                                        <StarRating
                                            rating={it.rating}
                                            interactive
                                            onChange={(r) => onUpdateRating(it.id, r)}
                                        />
                                        {it.ratingCount > 0 && (
                                            <span className="text-[10px] text-gray-400 font-medium">({it.ratingCount} {it.ratingCount === 1 ? 'vote' : 'votes'})</span>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="qr-title truncate flex-1">{it.name}</div>
                                        <div className="flex items-center gap-1">
                                            {playbook?.id && playbook.id !== 'local' && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log("Copying play link for:", it.id, "in playbook:", playbook.id);
                                                        const url = `${window.location.origin}/?play=${playbook.id}:${it.id}`;
                                                        navigator.clipboard.writeText(url);
                                                        if (addToast) addToast('Play link copied to clipboard!', 'info');
                                                    }}
                                                    className="text-[12px] opacity-40 hover:opacity-100 hover:text-green-400 transition-all p-1"
                                                    title="Copy Play Link"
                                                >
                                                    🔗
                                                </button>
                                            )}
                                            {canDuplicate && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onDuplicatePlay(it.id, it.name); }}
                                                    className="text-[12px] opacity-40 hover:opacity-100 hover:text-blue-400 transition-all p-1"
                                                    title="Duplicate Play"
                                                >
                                                    📑
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onDeletePlay(it.id, it.name); }}
                                                    className="text-[12px] opacity-40 hover:opacity-100 hover:text-red-400 transition-all p-1"
                                                    title="Delete Play"
                                                >
                                                    🗑️
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="qr-meta">
                                        <span className="qr-badge">{it.type}</span>
                                        <span className="qr-badge">{it.formation}</span>
                                    </div>
                                    <div className="qr-date-group flex flex-col gap-0.5 mt-auto pt-1">
                                        {it.createdAt && (
                                            <div className="qr-date text-[10px] opacity-70">
                                                Created: {new Date(it.createdAt).toLocaleDateString()} {it.createdByName && <span className="text-gray-400 italic">by {it.createdByName}</span>}
                                            </div>
                                        )}
                                        <div className="qr-date text-[10px] text-blue-400 font-medium">
                                            Updated: {new Date(it.savedAt || Date.now()).toLocaleDateString()} {it.updatedByName && <span className="text-gray-300 italic">by {it.updatedByName}</span>}
                                        </div>
                                    </div>
                                </div>

                                {sortBy === 'order' && (
                                    <div className="qr-order-controls">
                                        <button
                                            className="qr-order-btn"
                                            disabled={idx === 0}
                                            onClick={(e) => { e.stopPropagation(); onMovePlay(it.id, -1); }}
                                            title="Move Left"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                                        </button>
                                        <div className="w-px bg-white/10 h-6 self-center" />
                                        <button
                                            className="qr-order-btn"
                                            disabled={idx === items.length - 1}
                                            onClick={(e) => { e.stopPropagation(); onMovePlay(it.id, 1); }}
                                            title="Move Right"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
